import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../atoms/Button/Button.component";
import Icon from "../../atoms/Icon/Icon.component";
import Eyebrow from "../../atoms/Eyebrow/Eyebrow.styled";
import { useScrollLock } from "../../../utils/useScrollLock";
import { TENDBE_BUSINESS_EMAIL } from "../../../constants/navigation";
import {
  DialogActions,
  DialogBackdrop,
  DialogClose,
  DialogDescription,
  DialogNote,
  DialogPanel,
  DialogTitle,
} from "./DemoDialog.styled";

interface IDemoDialog {
  isOpen: boolean;
  plan?: string;
  onClose: () => void;
}

/**
 * Demo request dialog. It composes an email draft to the business address -
 * it does not book a meeting or send anything automatically. Replace the
 * email action once an actual booking flow is supplied.
 */
const DemoDialog = ({ isOpen, plan, onClose }: IDemoDialog) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setNote(t("landing.dialog.note"));
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose, t]);

  if (!isOpen) {
    return null;
  }

  const requestedPlan = plan || t("landing.dialog.defaultPlan");
  const subject = encodeURIComponent(
    t("landing.dialog.mailSubject", { plan: requestedPlan })
  );
  const body = encodeURIComponent(
    t("landing.dialog.mailBody", { plan: requestedPlan })
  );

  const onCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(TENDBE_BUSINESS_EMAIL);
      setNote(t("landing.dialog.copied"));
    } catch (error) {
      setNote(t("landing.dialog.copyFallback"));
    }
  };

  const onBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!panelRef.current?.contains(event.target as Node)) {
      onClose();
    }
  };

  return (
    <DialogBackdrop onMouseDown={onBackdropClick}>
      <DialogPanel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-dialog-title"
      >
        <DialogClose
          ref={closeRef}
          type="button"
          aria-label={t("landing.dialog.close")}
          onClick={onClose}
        >
          <Icon name="close" />
        </DialogClose>
        <Eyebrow as="div">{t("landing.dialog.eyebrow")}</Eyebrow>
        <DialogTitle id="demo-dialog-title">
          {t("landing.dialog.title")}
        </DialogTitle>
        <DialogDescription>{t("landing.dialog.description")}</DialogDescription>
        <DialogActions>
          <Button
            href={`mailto:${TENDBE_BUSINESS_EMAIL}?subject=${subject}&body=${body}`}
            icon="mail"
          >
            {t("landing.dialog.emailCta")}
          </Button>
          <Button variant="outline" icon={null} onClick={onCopyEmail}>
            {t("landing.dialog.copyCta")}
          </Button>
        </DialogActions>
        <DialogNote aria-live="polite">{note}</DialogNote>
      </DialogPanel>
    </DialogBackdrop>
  );
};

export default DemoDialog;
